/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { Suspense, useMemo, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { DndContext, DragOverlay, useDroppable, type DragStartEvent } from '@dnd-kit/core';
import { useCircles, useVerticals } from '@/queries/session';
import { useBoardTasks, useMoveTask } from '@/queries/tasks';
import { DONE_VERTICAL, type Task, type VerticalId } from '@/types/task';
import { COUNT, DOT, tokenOf, washFor } from './verticalStyles';
import { applyFilters, sortTasks, useBoardFilters } from './useBoardFilters';
import { TaskCard } from './TaskCard';
import { DoneColumnInfo } from './DoneColumnNote';
import { droppedVertical, useBoardSensors, useClickAfterDragGuard } from './dnd';
import { cn } from '@/lib/cn';

/**
 * The circle's columns.
 *
 * **There were five, hardcoded.** A circle chooses its own now — between one and
 * eight, the last of them always the done column — and they arrive with the
 * session rather than on a request of their own, so the board never paints five
 * defaults and then rearranges.
 *
 * Column widths follow the measured arithmetic in plan §7.2: five at 230px plus
 * four 14px gaps plus the page padding needs 1270px, so they narrow to 180px
 * below that and only wrap once a card would stop being readable. That
 * arithmetic assumed exactly five, so the fixed five-column tracks became
 * `auto-fit` with the same minimums — a board of seven narrows, a board of two
 * does not stretch to half the screen each. On a phone they scroll sideways as a
 * strip rather than reflowing: a kanban that wraps stops reading as stages.
 */
export function BoardView() {
  const { data: circles } = useCircles();
  const circleId = circles?.activeId ?? '';
  const columns = useVerticals();
  const { filters } = useBoardFilters();
  const sensors = useBoardSensors();
  const guard = useClickAfterDragGuard();
  const move = useMoveTask(circleId);
  const [dragging, setDragging] = useState<Task | null>(null);

  // `isLoading` — data has never arrived. NOT `isFetching`, which is also true
  // during the 20-second background refresh and would flash the whole board every
  // time. Plan §7.5, rule 2.
  const { data, isLoading, error } = useBoardTasks(circleId, circles !== undefined);

  const byVertical = useMemo(() => {
    const groups = new Map<VerticalId, Task[]>(columns.map((v) => [v.id, []]));
    const visible = sortTasks(applyFilters(data ?? [], filters, columns), filters.sort);
    // `?.push` — a task whose column this circle no longer has is dropped from
    // the board rather than crashing it. The server deletes such tasks with the
    // column, so this is the window between two polls, not a resting state.
    for (const task of visible) groups.get(task.vertical)?.push(task);
    return groups;
  }, [data, filters, columns]);

  if (error) {
    return (
      <div className="rounded-card border border-danger/30 bg-danger/5 p-8 text-center">
        <p className="m-0 text-base font-semibold text-danger">The board could not be loaded.</p>
        <p className="mt-1.5 mb-0 text-sm text-ink-muted">
          It will try again on its own in a few seconds.
        </p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(e: DragStartEvent) => {
        guard.markDragged();
        setDragging((data ?? []).find((t) => t.id === e.active.id) ?? null);
      }}
      onDragEnd={(e) => {
        guard.markDragged();
        setDragging(null);
        const vertical = droppedVertical(e);
        const task = (data ?? []).find((t) => t.id === e.active.id);
        // Dropping a card back where it started is not a move, and asking the
        // server would be a write and a notification for nothing.
        if (!vertical || !task || task.vertical === vertical) return;
        move.mutate({ id: task.id, vertical });
      }}
      onDragCancel={() => setDragging(null)}
    >
      <div
        className={cn(
          // `board` is the vanilla container's name and several suites scope
          // their counting to it — `.board .card`. Without it that selector
          // matches nothing *anywhere*, so those assertions compare zero against
          // zero and report whatever the arithmetic happens to make true. The
          // contract script called it present, because `.board` is a prefix of
          // `board-title`, `board-search` and `board-count`.
          'board grid gap-3.5 items-stretch',
          // Phone: a horizontally scrolling strip of readable columns.
          'grid-flow-col auto-cols-[80%] overflow-x-auto pb-2',
          // Tablet up: real columns, no page-level scrolling.
          'tablet:grid-flow-row tablet:auto-cols-auto tablet:overflow-visible tablet:pb-0',
          // `auto-fit`, not a fixed count: the number of columns is the circle's
          // to decide. The minimums are the measured ones from §7.2 and are what
          // actually protect readability — five still lay out exactly as before.
          'tablet:grid-cols-[repeat(auto-fit,minmax(240px,1fr))]',
          'wide:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]',
          'full:grid-cols-[repeat(auto-fit,minmax(230px,1fr))]',
        )}
      >
        {columns.map((v) => (
          <Column
            key={v.id}
            id={v.id}
            label={v.label}
            token={tokenOf(v)}
            tasks={byVertical.get(v.id) ?? []}
            loading={isLoading}
            guard={guard}
            retentionDays={(data ?? [])[0]?.retentionDays}
          />
        ))}
      </div>

      {/* Follows the pointer; the original stays faded in its column so nothing
          reflows underneath the drop you are aiming at. */}
      <DragOverlay dropAnimation={null}>
        {dragging ? (
          <div className="w-[260px] rotate-2 opacity-95 shadow-overlay">
            <TaskCard task={dragging} />
          </div>
        ) : null}
      </DragOverlay>

      {/* The task modal, which is a nested route. It renders here so the board
          stays mounted behind it — closing is a history step back, not a
          re-fetch of everything.

          **Its own Suspense boundary, and that is the point of it.** The modal is
          fetched on demand. Without a boundary here the suspension would travel
          up to the one around `AppLayout`'s outlet, which would replace the
          entire board with a blank box for as long as the chunk took — the board
          disappearing because you clicked a card on it. `null` because the board
          is already the right thing to be looking at while the modal arrives, and
          in practice it has been prefetched before anybody gets here. */}
      <Suspense fallback={null}>
        <Outlet />
      </Suspense>
    </DndContext>
  );
}

function Column({
  id,
  label,
  token,
  tasks,
  loading,
  guard,
  retentionDays,
}: {
  id: VerticalId;
  label: string;
  token: string;
  tasks: Task[];
  loading: boolean;
  guard: ReturnType<typeof useClickAfterDragGuard>;
  retentionDays?: number;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  const isDone = id === DONE_VERTICAL;

  return (
    <section className="column flex min-w-0 flex-col" data-column={id}>
      {/* **The `+` sits with the column, not at the far end of its header.**
          It was pushed right by `justify-between`, which on a 230px column left
          it a full column-width away from the name of the thing it adds to —
          close enough to the next column's edge to read as belonging to the gap
          rather than to either side. Beside the count it is unambiguous, and the
          header stops being two things held apart by empty space. */}
      <header className="flex items-center gap-2 px-1.5 pb-2.5 pt-0.5">
        {/* The dot with its own colour spread thinly around it. A 8px disc on a
            pale page is a full stop; the ring gives the colour enough area to
            register as this column's colour at a glance, without making the dot
            itself bigger and louder. */}
        <span className={cn('size-2 shrink-0 rounded-full ring-4', DOT[token])} />
        <h2 className="col-title m-0 text-sm font-semibold tracking-[-0.01em]">{label}</h2>
        {/* Tinted with the column's own colour rather than a universal grey.
            Five identical grey counts made the five headers one repeated shape;
            this is the cheapest place on the board to say which column you are
            reading without adding anything to it. */}
        <span className={cn('col-count rounded-full px-1.5 text-2xs font-semibold', COUNT[token])}>
          {loading ? '·' : tasks.length}
        </span>

        {/* Only the done column has anything to explain, and only because a
            circle may rename it. See web/src/board/DoneColumnNote.tsx for what
            it says and why it is behind a button rather than under the heading. */}
        {isDone && <DoneColumnInfo retentionDays={retentionDays} label={label} />}

        {/* "New task" makes a task; this makes one *here*. Worth both, because
            picking the column afterwards from a dropdown is the slower half of
            writing something down. */}
        <Link
          to={`/board/task/new?vertical=${id}`}
          aria-label={`Add task to ${label}`}
          title={`Add task to ${label}`}
          className="col-add ml-0.5 no-underline"
        >
          {/* A "+" character, as vanilla draws it — not an SVG. The two look
              near enough identical and the character costs no markup, but they
              are not the same to a screen reader or to a text diff, and this is
              the control every column header repeats five times. */}
          +
        </Link>
      </header>

      {/* The whole column is the drop target, not the last card in it. With a
          shorter list, dropping onto the empty space below silently did nothing. */}
      <div
        ref={setNodeRef}
        // `data-vertical` as well as the section's `data-column`: board-view
        // reaches for `.col-body[data-vertical="discovery"]` to aim a drop at the
        // body rather than at the heading above it.
        data-vertical={id}
        className={cn(
          'col-body flex flex-1 flex-col gap-2.5 rounded-xl p-1.5 transition-colors duration-fast ease-out-soft',
          washFor(id),
          isOver && 'ring-2 ring-action/40',
        )}
      >
        {loading ? (
          // Card-shaped skeletons, not a spinner, so the layout does not jump when
          // the data lands.
          <>
            <Skeleton />
            <Skeleton />
          </>
        ) : tasks.length === 0 ? (
          // Faint on purpose. On the white tray this used to be a hard dashed
          // rectangle in every empty column — four of them on a quiet board,
          // each drawing more attention than the columns that had work in them.
          <div className="rounded-card border border-dashed border-border/60 py-8 text-center text-xs text-ink-faint">
            No tasks yet
          </div>
        ) : (
          tasks.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              draggable
              onClickCapture={(e) => {
                // The click that ends a drag would otherwise open the task that
                // was just dropped.
                if (guard.shouldSwallowClick()) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            />
          ))
        )}
      </div>
    </section>
  );
}

function Skeleton() {
  return (
    <div className="rounded-card border border-border bg-surface p-3">
      <div className="h-3.5 w-3/4 rounded bg-black/[.07]" />
      <div className="mt-2 h-2.5 w-1/2 rounded bg-black/[.05]" />
    </div>
  );
}

/**
 * DOT, COUNT and WASH used to live here, keyed by the five column ids. They are
 * in ./verticalStyles.ts now, keyed by **palette token** — an id a circle
 * invented is not a colour, and Tailwind cannot see a class built by template
 * string, so every one of them has to exist as a literal in one place.
 */
